import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';

// Call state interface
interface ActiveCall {
  callSid: string | null;
  customerId: number;
  customerName: string;
  customerPhone: string;
  status: 'connecting' | 'ringing' | 'in-progress' | 'completed';
  startTime: Date;
  duration: number;
}

// Pending incoming call interface (before agent accepts/rejects)
interface PendingIncomingCall {
  call: Call;  // Twilio Call object
  callerPhone: string;
  callerName: string | null;  // Customer name if found
  customerId: number | null;  // Customer ID if found
  callSid: string | null;
  receivedAt: Date;
}

interface CallState {
  isReady: boolean;
  isInitializing: boolean;
  activeCall: ActiveCall | null;
  pendingIncomingCall: PendingIncomingCall | null;
  isCallInProgress: boolean;
  isMuted: boolean;
  volume: number;
  error: string | null;
  twilioConfigured: boolean | null;
}

interface CallActions {
  initializeDevice: (options?: { silent?: boolean }) => Promise<void>;
  makeCall: (customer: {
    id: number;
    name: string;
    phone: string;
  }) => Promise<void>;
  hangup: () => void;
  toggleMute: () => void;
  setVolume: (volume: number) => void;
  acceptIncomingCall: () => void;
  rejectIncomingCall: () => void;
}

const CallContext = createContext<(CallState & CallActions) | undefined>(undefined);

export function CallProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const { user } = useAuth();

  // Twilio Device instance (ref to persist across renders)
  const deviceRef = useRef<Device | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const pendingIncomingCallRef = useRef<PendingIncomingCall | null>(null);  // Fix #4: Track pending calls in ref for concurrent call handling
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializingRef = useRef(false);

  // Track recent call attempt for transient error suppression (Issue 1 fix)
  const recentCallAttemptRef = useRef<number | null>(null);
  const TRANSIENT_ERROR_GRACE_PERIOD_MS = 5000;
  const TRANSIENT_ERROR_CODES = [31005, 31009]; // ConnectionError codes

  // Track last hangup time to prevent auto-callback (Issue 2 fix)
  const lastHangupTimeRef = useRef<number | null>(null);
  const HANGUP_COOLDOWN_MS = 3000; // 3 second cooldown after hangup

  // State
  const [isReady, setIsReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [pendingIncomingCall, setPendingIncomingCall] = useState<PendingIncomingCall | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const [error, setError] = useState<string | null>(null);
  const [twilioConfigured, setTwilioConfigured] = useState<boolean | null>(null);

  // Helper function to lookup customer by phone number
  // Fix #1: Use apiRequest instead of fetch to include auth credentials
  const lookupCustomerByPhone = useCallback(async (phone: string): Promise<{ id: number; name: string } | null> => {
    try {
      const data = await apiRequest<{ found: boolean; customer: { id: number; name: string } | null }>(
        'GET',
        `/api/twilio/lookup-customer?phone=${encodeURIComponent(phone)}`
      );
      return data.found ? data.customer : null;
    } catch (err) {
      console.error('Error looking up customer:', err);
      return null;
    }
  }, []);

  // Start duration timer (defined before useEffect that uses it)
  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }

    durationIntervalRef.current = setInterval(() => {
      setActiveCall((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          duration: Math.floor((Date.now() - prev.startTime.getTime()) / 1000),
        };
      });
    }, 1000);
  }, []);

  // Stop duration timer (defined before useEffect that uses it)
  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  // Lazy initialization function - only called when user clicks Call button
  const initializeDevice = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;

    // Prevent multiple simultaneous initialization attempts
    if (isInitializingRef.current || deviceRef.current) {
      console.log('Device already initializing or initialized');
      return;
    }

    isInitializingRef.current = true;
    setIsInitializing(true);
    setError(null);

    // Set timeout for initialization (15 seconds)
    initializationTimeoutRef.current = setTimeout(() => {
      if (!isReady) {
        console.error('Twilio initialization timed out after 15 seconds');
        setIsInitializing(false);
        isInitializingRef.current = false;
        setError('Initialization timeout - please check your connection and try again');
        if (!silent) {
          toast({
            title: 'Initialization Timeout',
            description: 'Could not connect to calling system. Please try again.',
            variant: 'destructive',
          });
        }
      }
    }, 15000);

    try {
      console.log('Checking Twilio configuration...');

      // First, check if Twilio is properly configured
      const configResponse = await fetch('/api/twilio/config-status');
      const configData = await configResponse.json();

      if (!configData.configured) {
        console.log('Twilio not configured');
        console.log('Missing credentials:', configData.missing);
        setIsInitializing(false);
        isInitializingRef.current = false;
        setIsReady(false);
        setTwilioConfigured(false);
        setError('Calling system not configured. Please contact your administrator.');
        if (initializationTimeoutRef.current) {
          clearTimeout(initializationTimeoutRef.current);
        }
        if (!silent) {
          toast({
            title: 'Calling Not Available',
            description: 'The calling system is not configured. Please contact your administrator.',
            variant: 'destructive',
          });
        }
        return;
      }

      console.log('Twilio configured, initializing Device...');

      // Fetch capability token from backend
      let response;
      try {
        response = await apiRequest<{ token: string; identity: string }>(
          'GET',
          '/api/twilio/token'
        );
      } catch (tokenError: any) {
        // Handle authentication errors gracefully (user not logged in yet)
        const isAuthError =
          tokenError instanceof Error &&
          (tokenError.message?.includes('401') || tokenError.message?.toLowerCase().includes('unauthorized'));

        if (isAuthError) {
          console.log('User not authenticated — skipping Twilio init silently');
          setIsInitializing(false);
          isInitializingRef.current = false;
          setIsReady(false);
          setError('Please log in to use the calling system');
          if (initializationTimeoutRef.current) {
            clearTimeout(initializationTimeoutRef.current);
          }
          // Toast intentionally removed — no UI noise on login page
          return;
        }
        // Re-throw other errors to be handled by outer catch
        throw tokenError;
      }

      // Create new Device instance
      const device = new Device(response.token, {
        logLevel: 1, // Warn level
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      // Set up event listeners
      device.on('registered', () => {
        console.log('Twilio Device registered and ready');
        setIsReady(true);
        setIsInitializing(false);
        isInitializingRef.current = false;
        setError(null);
        if (initializationTimeoutRef.current) {
          clearTimeout(initializationTimeoutRef.current);
        }
      });

      device.on('error', (error) => {
        console.error('Twilio Device error:', error);

        // Check if within grace period for transient errors (Issue 1 fix)
        const isWithinGracePeriod = recentCallAttemptRef.current &&
          (Date.now() - recentCallAttemptRef.current) < TRANSIENT_ERROR_GRACE_PERIOD_MS;
        const isTransientError = TRANSIENT_ERROR_CODES.includes(error.code);

        // Suppress transient errors during call setup grace period
        if (isWithinGracePeriod && isTransientError) {
          console.log('Suppressing transient error during call setup:', error.code, error.message);
          return;
        }

        // Check if there's an active call that's still connected
        // Twilio can emit transient device errors (like 31005) during call setup
        const currentCall = activeCallRef.current;
        if (currentCall) {
          const callStatus = currentCall.status();
          const isCallStillActive = callStatus === 'open' || callStatus === 'connecting' || callStatus === 'ringing';

          if (isCallStillActive) {
            // Transient device error during active call - just log it
            console.log('Transient device error (call still active):', error.code, error.message, 'Call status:', callStatus);
            return;
          }
        }

        // Device error with no active call, or call has failed - notify user
        setError(error.message);
        setIsInitializing(false);
        isInitializingRef.current = false;
        if (initializationTimeoutRef.current) {
          clearTimeout(initializationTimeoutRef.current);
        }
        toast({
          title: 'Call System Error',
          description: error.message,
          variant: 'destructive',
        });
      });

      device.on('tokenWillExpire', async () => {
        console.log('Token will expire, refreshing...');
        try {
          const refreshResponse = await apiRequest<{ token: string }>(
            'GET',
            '/api/twilio/token'
          );
          device.updateToken(refreshResponse.token);
          console.log('Token refreshed successfully');
        } catch (err) {
          console.error('Failed to refresh token:', err);
        }
      });

      // Handle incoming calls (HYBRID ROUTING with Accept/Reject UI)
      device.on('incoming', (incomingCall) => {
        console.log('INCOMING CALL from:', incomingCall.parameters.From);

        // Check hangup cooldown (Issue 2 fix - prevent auto-callback after hangup)
        if (lastHangupTimeRef.current) {
          const timeSinceHangup = Date.now() - lastHangupTimeRef.current;
          if (timeSinceHangup < HANGUP_COOLDOWN_MS) {
            console.log('Rejecting call during cooldown period:', timeSinceHangup, 'ms since hangup');
            incomingCall.reject();
            toast({
              title: 'Call Blocked',
              description: 'Incoming call blocked during cooldown period after hangup',
            });
            return;
          }
        }

        // Fix #4: Check for both active call AND pending call before accepting new incoming
        if (activeCallRef.current) {
          console.log('Already on a call, rejecting incoming');
          incomingCall.reject();
          return;
        }

        // Check if already have a pending call - reject new one to avoid overwriting
        // Note: We check the ref state at the time of the event, not React state
        // because React state may be stale in this callback
        if (pendingIncomingCallRef.current) {
          console.log('Already have pending call, rejecting new incoming');
          incomingCall.reject();
          return;
        }

        // Extract caller info
        const callerPhone = incomingCall.parameters.From || 'Unknown';
        const callSid = incomingCall.parameters.CallSid || null;

        // Fix #2: Track if call was cancelled before async lookup completes
        let callWasCancelled = false;

        // Set up cancel handler BEFORE starting async lookup (caller hangs up before agent answers)
        incomingCall.on('cancel', () => {
          console.log('Incoming call canceled by caller');
          callWasCancelled = true;  // Mark as cancelled for race condition guard
          setPendingIncomingCall(null);
          pendingIncomingCallRef.current = null;  // Fix #4: Keep ref in sync
          setActiveCall(null);
          activeCallRef.current = null;

          toast({
            title: 'Missed Call',
            description: `Call from ${callerPhone} was canceled`,
          });
        });

        // Handle errors on pending call
        incomingCall.on('error', (error: any) => {
          console.error('Incoming call error:', error);
          callWasCancelled = true;  // Treat error as "don't set pending"
          setPendingIncomingCall(null);
          pendingIncomingCallRef.current = null;  // Fix #4: Keep ref in sync
          stopDurationTimer();
          setActiveCall(null);
          activeCallRef.current = null;

          toast({
            title: 'Call Error',
            description: error.message || 'Error with incoming call',
            variant: 'destructive',
          });
        });

        // DON'T auto-accept - store as pending and show Accept/Reject UI
        // Look up customer info from API
        lookupCustomerByPhone(callerPhone).then(customer => {
          // Fix #2: Guard against race condition - only set pending if call wasn't cancelled
          // during the async lookup AND call is still in valid state
          if (callWasCancelled) {
            console.log('Customer lookup completed but call was already cancelled');
            return;
          }

          const callStatus = incomingCall.status();
          if (callStatus === 'closed' || callStatus === 'pending') {
            console.log('Customer lookup completed but call status is:', callStatus);
            return;
          }

          const pendingCall: PendingIncomingCall = {
            call: incomingCall,
            callerPhone,
            callerName: customer?.name || null,
            customerId: customer?.id || null,
            callSid,
            receivedAt: new Date(),
          };
          setPendingIncomingCall(pendingCall);
          pendingIncomingCallRef.current = pendingCall;  // Fix #4: Keep ref in sync
        });
      });

      // Register the device
      await device.register();

      // Store device in ref
      deviceRef.current = device;
    } catch (err: any) {
      console.error('Failed to initialize Twilio Device:', err);

      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize calling system';
      setError(errorMessage);
      setIsInitializing(false);
      isInitializingRef.current = false;

      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }

      if (!silent) {
        toast({
          title: 'Call System Error',
          description: 'Could not initialize browser calling. Please try again.',
          variant: 'destructive',
        });
      }
    }
  }, [toast, startDurationTimer, stopDurationTimer, isReady]);

  // Check Twilio config status once on mount (when user exists)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    fetch('/api/twilio/config-status')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) {
          setTwilioConfigured(data.configured === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTwilioConfigured(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Fix E: Eager Device initialization — start connecting 2s after mount
  // so the WebSocket is ready before the user clicks "Call".
  // Only runs when Twilio is confirmed configured.
  useEffect(() => {
    if (!user) return;
    if (twilioConfigured !== true) return; // Skip when unconfigured or still checking

    const timer = setTimeout(() => {
      // Idempotency: initializeDevice() already guards against double-init
      initializeDevice({ silent: true }).catch(() => {
        // Swallow errors — lazy init on first call click still works as fallback
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [initializeDevice, user, twilioConfigured]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Fix #5: Reject any pending call on unmount to prevent memory leaks
      if (pendingIncomingCallRef.current?.call) {
        try {
          pendingIncomingCallRef.current.call.reject();
          console.log('Rejected pending call on unmount');
        } catch (e) {
          // Call may already be closed, ignore error
        }
        pendingIncomingCallRef.current = null;
      }

      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, []);

  // Make a call
  const makeCall = useCallback(
    async (customer: { id: number; name: string; phone: string }) => {
      // If device not initialized yet, initialize it first
      if (!deviceRef.current && !isInitializingRef.current) {
        console.log('Device not ready, initializing...');
        await initializeDevice();
      }

      // Wait a moment for initialization to complete if it just started
      if (isInitializingRef.current) {
        // Wait up to 20 seconds for initialization
        let attempts = 0;
        while (isInitializingRef.current && attempts < 40) {
          await new Promise(resolve => setTimeout(resolve, 500));
          attempts++;
        }
      }

      if (!deviceRef.current) {
        toast({
          title: 'Call System Not Ready',
          description: error || 'Could not initialize calling system. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      if (activeCall) {
        toast({
          title: 'Call In Progress',
          description: 'Please end the current call before starting a new one.',
          variant: 'destructive',
        });
        return;
      }

      try {
        console.log('Making call to:', customer.phone);

        // Set grace period for transient error suppression (Issue 1 fix)
        recentCallAttemptRef.current = Date.now();

        // Connect to customer
        const call = await deviceRef.current.connect({
          params: {
            To: customer.phone,
          },
        });

        activeCallRef.current = call;

        // Initialize call state
        setActiveCall({
          callSid: null, // Will be set when we get it from Twilio
          customerId: customer.id,
          customerName: customer.name,
          customerPhone: customer.phone,
          status: 'connecting',
          startTime: new Date(),
          duration: 0,
        });

        // Safety net: auto-cancel calls stuck in connecting state after 30s
        const connectTimeout = setTimeout(() => {
          if (call.status() === 'connecting') {
            console.warn('Call stuck in connecting state for 30s, auto-cancelling');
            try { call.disconnect(); } catch (e) { /* ignore */ }
            stopDurationTimer();
            setActiveCall(null);
            activeCallRef.current = null;
            toast({
              title: 'Call Failed',
              description: 'Could not connect. Please check the number and try again.',
              variant: 'destructive',
            });
          }
        }, 30000);

        // Set up call event listeners
        call.on('accept', (acceptedCall) => {
          console.log('Call accepted:', acceptedCall.parameters.CallSid);
          clearTimeout(connectTimeout);
          setActiveCall((prev) =>
            prev
              ? {
                  ...prev,
                  callSid: acceptedCall.parameters.CallSid || null,
                  status: 'ringing',
                }
              : null
          );
        });

        call.on('ringing', () => {
          console.log('Call ringing');
          setActiveCall((prev) => (prev ? { ...prev, status: 'ringing' } : null));
        });

        call.on('connect', () => {
          console.log('Call connected');
          recentCallAttemptRef.current = null; // Clear grace period on successful connect
          setActiveCall((prev) => (prev ? { ...prev, status: 'in-progress' } : null));
          startDurationTimer();

          toast({
            title: 'Call Connected',
            description: `Connected to ${customer.name}`,
          });
        });

        call.on('disconnect', () => {
          console.log('Call disconnected');
          clearTimeout(connectTimeout);
          stopDurationTimer();
          setActiveCall(null);
          activeCallRef.current = null;

          toast({
            title: 'Call Ended',
            description: 'The call has been disconnected.',
          });
        });

        call.on('error', (error) => {
          console.error('Call error:', error.code, error.message);
          clearTimeout(connectTimeout);

          // Only suppress transient errors for ESTABLISHED calls (status 'open').
          // Errors during 'connecting'/'ringing' are fatal — webhook unreachable,
          // TwiML app not found, timeout, etc. The call will never establish.
          const callStatus = call.status();
          if (callStatus === 'open') {
            console.log('Transient call error (call established):', error.code, error.message);
            return;
          }

          // Call failed during setup or is no longer active — clean up
          stopDurationTimer();
          setActiveCall(null);
          activeCallRef.current = null;

          toast({
            title: 'Call Failed',
            description: error.message || 'The call could not be completed.',
            variant: 'destructive',
          });
        });
      } catch (err) {
        console.error('Failed to make call:', err);
        setActiveCall(null);
        toast({
          title: 'Call Failed',
          description: err instanceof Error ? err.message : 'Could not initiate call',
          variant: 'destructive',
        });
      }
    },
    [activeCall, toast, startDurationTimer, stopDurationTimer, initializeDevice, error]
  );

  // Hang up current call — matches official Twilio quickstart pattern:
  // call.disconnect() is the only needed client-side mechanism.
  // Synchronous (not async) for instant UI response.
  const hangup = useCallback(() => {
    // 1. Capture call ref and callSid BEFORE clearing state
    const call = activeCallRef.current;
    const callSid = activeCall?.callSid || call?.parameters?.CallSid || null;

    // 2. Clear UI state immediately
    stopDurationTimer();
    setActiveCall(null);
    setIsMuted(false);
    activeCallRef.current = null;
    lastHangupTimeRef.current = Date.now();
    recentCallAttemptRef.current = null;

    // 3. Disconnect the call (primary — per official Twilio quickstart)
    if (call) {
      try {
        call.disconnect();
      } catch (err) {
        console.warn('call.disconnect() failed:', err);
      }
    }

    // 4. Backend REST API as server-side fallback (ends both call legs)
    if (callSid) {
      apiRequest('POST', '/api/twilio/hangup', { callSid }).catch(() => {});
    }
  }, [stopDurationTimer, activeCall?.callSid]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (activeCallRef.current) {
      const newMutedState = !isMuted;
      activeCallRef.current.mute(newMutedState);
      setIsMuted(newMutedState);
      console.log('Mute toggled:', newMutedState);
    }
  }, [isMuted]);

  // Set volume
  const handleSetVolume = useCallback((newVolume: number) => {
    setVolume(newVolume);
    // Note: Twilio SDK doesn't have direct volume control
    // Volume is controlled by the user's device settings
    console.log('Volume set to:', newVolume);
  }, []);

  // Accept incoming call
  const acceptIncomingCall = useCallback(() => {
    if (!pendingIncomingCall) return;

    const { call, callerPhone, callerName, customerId, callSid } = pendingIncomingCall;

    console.log('Accepting incoming call from:', callerPhone);

    // Fix #3 & #6: Track if disconnect handler has been called to prevent duplicates
    let disconnectHandled = false;

    // Set up disconnect handler BEFORE accepting (ensures handler is attached)
    // This replaces any handlers set during the incoming event
    call.on('disconnect', () => {
      // Fix #6: Guard against duplicate disconnect handling
      if (disconnectHandled) {
        console.log('Disconnect already handled, ignoring duplicate');
        return;
      }
      disconnectHandled = true;

      console.log('Call disconnected');
      stopDurationTimer();
      setActiveCall(null);
      activeCallRef.current = null;

      toast({
        title: 'Call Ended',
        description: 'The call has ended.',
      });
    });

    // Accept the Twilio call
    call.accept();

    // Move to active call state
    setActiveCall({
      callSid,
      customerId: customerId || 0,
      customerName: callerName || `Incoming: ${callerPhone}`,
      customerPhone: callerPhone,
      status: 'in-progress',
      startTime: new Date(),
      duration: 0,
    });

    activeCallRef.current = call;
    setPendingIncomingCall(null);
    pendingIncomingCallRef.current = null;  // Fix #4: Keep ref in sync
    lastHangupTimeRef.current = null; // Clear cooldown on successful accept
    startDurationTimer();

    toast({
      title: 'Call Connected',
      description: `Connected to ${callerName || callerPhone}`,
    });
  }, [pendingIncomingCall, startDurationTimer, stopDurationTimer, toast]);

  // Reject incoming call
  const rejectIncomingCall = useCallback(() => {
    if (!pendingIncomingCall) return;

    console.log('Rejecting incoming call from:', pendingIncomingCall.callerPhone);

    pendingIncomingCall.call.reject();
    setPendingIncomingCall(null);
    pendingIncomingCallRef.current = null;  // Fix #4: Keep ref in sync

    toast({
      title: 'Call Rejected',
      description: 'Incoming call was rejected',
    });
  }, [pendingIncomingCall, toast]);

  const value = {
    isReady,
    isInitializing,
    activeCall,
    pendingIncomingCall,
    isCallInProgress: activeCall !== null,
    isMuted,
    volume,
    error,
    twilioConfigured,
    initializeDevice,
    makeCall,
    hangup,
    toggleMute,
    setVolume: handleSetVolume,
    acceptIncomingCall,
    rejectIncomingCall,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

// Hook to use call context
// Returns null if used outside CallProvider (handles initialization race conditions)
export function useCall() {
  const context = useContext(CallContext);
  if (context === undefined) {
    // Return null instead of throwing to handle React initialization race conditions
    // Components using this hook should check for null before accessing properties
    return null;
  }
  return context;
}
